import type { OpsFlow } from '../types';

export type OpsFlowPriority = {
  flowId: string;
  squadId: string;
  risk: number; // 0-10
  dueUrgency: number; // 0-10
  needsDueDate: boolean;
  priorityScore: number; // 0-100
  /**
   * True when this flow has at least one checkpoint in its applied snapshots.
   * Used to distinguish “finished” (all checkpoints complete) from “uncategorized/no template”.
   */
  hasAnyCheckpoint?: boolean;
  /**
   * True when this flow has checkpoints and none are currently < 100%.
   */
  allCheckpointsComplete?: boolean;
  currentCheckpoint: {
    checkpointId: string;
    title: string;
    percent: number; // 0-100
    dueDateYmd?: string;
    daysToDue: number | null;
  } | null;
  overallPercentComplete: number; // 0-100
};

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function clamp0to10(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 10) return 10;
  return n;
}

function parseYmdToUtcMidnight(ymd: string): Date | null {
  const s = String(ymd || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  const dt = new Date(Date.UTC(y, mo - 1, d, 0, 0, 0, 0));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function daysToDueUtc(ymd: string, now: Date): number | null {
  const due = parseYmdToUtcMidnight(ymd);
  if (!due) return null;
  const dayMs = 24 * 60 * 60 * 1000;
  const nowMidnight = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
  return Math.round((due.getTime() - nowMidnight.getTime()) / dayMs);
}

export function dueUrgencyFromDaysToDue(daysToDue: number | null): { dueUrgency: number; needsDueDate: boolean } {
  // Matches weekly report mapping
  // "No due date" should be treated as highly urgent (roughly equivalent to "Due in 2 days")
  // so it doesn't get deprioritized in triage.
  if (daysToDue === null) return { dueUrgency: 9, needsDueDate: true };
  if (daysToDue < 0) return { dueUrgency: 10, needsDueDate: false };
  if (daysToDue <= 1) return { dueUrgency: 10, needsDueDate: false };
  if (daysToDue <= 3) return { dueUrgency: 9, needsDueDate: false };
  if (daysToDue <= 7) return { dueUrgency: 7, needsDueDate: false };
  if (daysToDue <= 14) return { dueUrgency: 5, needsDueDate: false };
  if (daysToDue <= 30) return { dueUrgency: 3, needsDueDate: false };
  return { dueUrgency: 1, needsDueDate: false };
}

export function formatDueDelta(daysToDue: number | null): string {
  if (daysToDue === null) return 'No due date';
  if (daysToDue === 0) return 'Due today';
  if (daysToDue > 0) return `Due in ${daysToDue} day${daysToDue === 1 ? '' : 's'}`;
  const overdue = Math.abs(daysToDue);
  return `Overdue by ${overdue} day${overdue === 1 ? '' : 's'}`;
}

export function daysAgoShort(now: Date, tsMs?: number): string {
  const nowMs = now.getTime();
  const t = Number(tsMs);
  if (!Number.isFinite(t) || t <= 0) return '—';
  const dayMs = 24 * 60 * 60 * 1000;
  const days = Math.max(0, Math.floor((nowMs - t) / dayMs));
  return `${days}d ago`;
}

function computeOverallPercentComplete(flow: OpsFlow): number {
  const tasks = Object.values((flow as any)?.taskState || {});
  const active = tasks.filter((t: any) => String(t?.status || 'open') !== 'superseded');
  if (active.length === 0) return 0;
  const done = active.filter((t: any) => String(t?.status || 'open') === 'done').length;
  return Math.round(clamp01(done / active.length) * 100);
}

function computeCheckpointPercent(flow: any, checkpointDef: any): number {
  const tasks = Array.isArray(checkpointDef?.tasks) ? checkpointDef.tasks : [];
  const activeTaskIds = tasks
    .map((t: any) => String(t?.taskId || '').trim())
    .filter(Boolean)
    .filter((id: string) => String(flow?.taskState?.[id]?.status || 'open') !== 'superseded');
  if (activeTaskIds.length === 0) return 100;
  const doneCount = activeTaskIds.filter((id: string) => String(flow?.taskState?.[id]?.status || 'open') === 'done').length;
  return Math.round(clamp01(doneCount / activeTaskIds.length) * 100);
}

function currentCheckpointForFlow(flow: any, now: Date): { current: OpsFlowPriority['currentCheckpoint']; hasAny: boolean } {
  const sources = Array.isArray(flow?.appliedSources) ? flow.appliedSources : [];
  if (sources.length === 0) return { current: null, hasAny: false };

  const defsById = new Map<string, any>();
  for (const src of sources) {
    const snapshot = (src as any)?.snapshot || null;
    const cps = snapshot && Array.isArray((snapshot as any)?.checkpoints) ? ((snapshot as any).checkpoints as any[]) : [];
    for (const cp of cps) {
      const id = String(cp?.checkpointId || '').trim();
      if (!id) continue;
      defsById.set(id, cp); // latest metadata wins
    }
  }

  const ids = Array.from(defsById.keys());
  if (ids.length === 0) return { current: null, hasAny: false };

  ids.sort((a, b) => {
    const ao = Number(flow?.checkpointState?.[a]?.orderOverride ?? defsById.get(a)?.order ?? 0);
    const bo = Number(flow?.checkpointState?.[b]?.orderOverride ?? defsById.get(b)?.order ?? 0);
    if (ao !== bo) return ao - bo;
    return String(defsById.get(a)?.title || a).localeCompare(String(defsById.get(b)?.title || b));
  });

  for (const id of ids) {
    const st = String(flow?.checkpointState?.[id]?.status || '');
    if (st === 'superseded') continue;
    const def = defsById.get(id);
    const pct = computeCheckpointPercent(flow, def);
    if (pct < 100) {
      const title = String(def?.title || id).trim() || id;
      const dueDateYmd = String(flow?.checkpointState?.[id]?.dueDate || '').trim() || undefined;
      const daysToDue = dueDateYmd ? daysToDueUtc(dueDateYmd, now) : null;
      return { current: { checkpointId: id, title, percent: pct, dueDateYmd, daysToDue }, hasAny: true };
    }
  }

  return { current: null, hasAny: true };
}

export function scoreOpsFlowPriority(flow: OpsFlow, now: Date = new Date()): OpsFlowPriority {
  const risk = clamp0to10(Number((flow as any)?.riskScoreOutOf10 ?? 0));
  const cur = currentCheckpointForFlow(flow as any, now);
  const currentCheckpoint = cur.current;
  const hasAnyCheckpoint = cur.hasAny;
  const allCheckpointsComplete = hasAnyCheckpoint && !currentCheckpoint;
  const { dueUrgency, needsDueDate } = dueUrgencyFromDaysToDue(currentCheckpoint?.daysToDue ?? null);
  const priorityScore = (0.55 * dueUrgency + 0.45 * risk) * 10; // 0-100
  return {
    flowId: String((flow as any)?.id || (flow as any)?.squadId || ''),
    squadId: String((flow as any)?.squadId || (flow as any)?.id || ''),
    risk,
    dueUrgency,
    needsDueDate,
    priorityScore,
    hasAnyCheckpoint,
    allCheckpointsComplete,
    currentCheckpoint,
    overallPercentComplete: computeOverallPercentComplete(flow),
  };
}

/**
 * Sorting that matches the weekly report behavior.
 */
export function compareOpsFlowPriority(a: OpsFlowPriority, b: OpsFlowPriority): number {
  if (b.priorityScore !== a.priorityScore) return b.priorityScore - a.priorityScore; // desc
  const aDue = a.currentCheckpoint?.daysToDue ?? Number.POSITIVE_INFINITY;
  const bDue = b.currentCheckpoint?.daysToDue ?? Number.POSITIVE_INFINITY;
  if (aDue !== bDue) return aDue - bDue; // asc (overdue/earlier first)
  const aPct = a.currentCheckpoint?.percent ?? 100;
  const bPct = b.currentCheckpoint?.percent ?? 100;
  if (aPct !== bPct) return aPct - bPct; // asc
  if (b.risk !== a.risk) return b.risk - a.risk; // desc
  return a.flowId.localeCompare(b.flowId);
}

